/**
 * CorporatePosturePanel — "Corporate Security Posture" roll-up on the
 * Foundations dashboard. Replaces the prior single-project PrismInsightBanner
 * hero per Roger's 2026-06-04 request: surface PRISM's corporate roll-up
 * (the aggregate across all of the org's PRISM business-unit projects) here,
 * matching PRISM's own "Corporate Security Posture" view.
 *
 * Five stat cards: Total Projects · Corporate Coverage (+ P/D/R) · Controls
 * Evaluated · Evidence · Open Gaps.
 *
 * Data: GET /api/foundations/prism-data/:sessionId returns the org-level
 * aggregate (project_count, evidence_count, asset_coverage_pct, pdr_maturity)
 * plus `compliance_facts` (PRISM #291, pooled across projects). No compliance
 * percentage is shown (decided 2026-10-29).
 *
 * KNOWN PLACEHOLDER: the screenshot's headline is PRISM's DoDCAR ASM%
 * (attack_surface_mitigated_pct). That field is NOT synced to Foundations
 * today (the snapshot lacks it AND organizationRollup's sync-to-foundations is
 * a stub). So Corporate Coverage shows the honest `asset_coverage_pct` for now.
 * When the PRISM-side live sync ships, swap `asset_coverage_pct` → ASM% and the
 * sublabel "asset coverage" → "attack surface mitigated".
 *
 * Color-blind-safe: every card carries an icon + text label; the tone color is
 * decorative, never the sole signal (per Foundations a11y rule).
 *
 * Props:
 *   sessionId          — foundations session id
 *   companyName        — org name for the subtitle (optional)
 *   prismSignals       — drift payload (optional; powers the drift chip)
 *   isPortfolioManager — boolean; suppresses the panel for PM accounts
 */
import React, { useEffect, useState } from 'react';
import { Building2, Shield, ClipboardCheck, FileSearch, AlertTriangle } from 'lucide-react';
import { apiConfig } from '../../../config/apiConfig';
import { authHeaders } from '../../../services/authHeaders';
import styles from './CorporatePosturePanel.module.css';
import { complianceFactsDisplay } from '../../../shared/components/prismFacts/complianceFacts';

// node-pg returns numeric/decimal columns as strings — coerce before math.
function toNum(v) {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const fmtPct = (v) => (v == null ? '—' : `${Math.round(v)}%`);
const fmtInt = (v) => (v == null ? '—' : Math.round(v).toLocaleString());

export default function CorporatePosturePanel({ sessionId, companyName, prismSignals, isPortfolioManager = false }) {
  const [prismData, setPrismData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId || isPortfolioManager) return;
    let cancelled = false;
    setLoading(true);

    fetch(apiConfig.foundations.getPrismData(sessionId), {
      headers: authHeaders(),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled && data.success && data.data?.connected !== false) {
          setPrismData(data.data);
        }
      })
      .catch(() => { /* PRISM unavailable — panel stays hidden */ })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [sessionId, isPortfolioManager]);

  // PM accounts get PRISM signals in Weekly Scan columns (US-FA-11-a), not here.
  if (isPortfolioManager) return null;
  if (loading || !prismData || prismData.connected === false) return null;

  const {
    project_count = 1,
    asset_coverage_pct = null,
    evidence_count = 0,
    compliance_facts = null,
    frameworks_assessed = [],
    pdr_maturity = {},
  } = prismData;

  const projects = toNum(project_count) ?? 1;
  const coverage = toNum(asset_coverage_pct);
  const evidence = toNum(evidence_count) ?? 0;
  // 2026-10-29: no compliance % outside SPRS. The "Compliance X%" card is now % of applicable
  // controls EVALUATED (progress, not compliance), and the gaps card reads PRISM's open gaps.
  // It used to read gap_count (total − met − partial), which counted unassessed controls as
  // gaps and was labelled "Critical". Absent or old-shape facts → "Not measured", never 0.
  const facts = complianceFactsDisplay(compliance_facts);
  const fwCount = Array.isArray(frameworks_assessed) ? frameworks_assessed.length : 0;

  const p = toNum(pdr_maturity.protect);
  const d = toNum(pdr_maturity.detect);
  const r = toNum(pdr_maturity.respond);
  const hasPdr = p != null || d != null || r != null;

  const drift = prismSignals?.drift || null;
  const critical = toNum(drift?.critical_open) ?? 0;
  const high = toNum(drift?.high_open) ?? 0;
  const openDrift = critical + high;

  const cards = [
    {
      key: 'projects', tone: 'slate', icon: Building2,
      label: 'Total Projects', value: fmtInt(projects), sub: `${projects} active`,
    },
    {
      key: 'coverage', tone: 'violet', icon: Shield,
      label: 'Corporate Coverage', value: fmtPct(coverage), sub: 'asset coverage',
      bar: coverage, pdr: hasPdr,
    },
    {
      key: 'compliance', tone: 'green', icon: ClipboardCheck,
      label: 'Controls Evaluated', value: facts.evaluated,
      sub: facts.measured ? facts.evaluatedDetail : `${fwCount} framework${fwCount === 1 ? '' : 's'}`,
      bar: facts.evaluatedPct,
    },
    {
      key: 'evidence', tone: 'cyan', icon: FileSearch,
      label: 'Evidence', value: fmtInt(evidence), sub: 'AI-analyzed',
    },
    {
      key: 'gaps', tone: 'red', icon: AlertTriangle,
      label: 'Open Gaps', value: facts.openGaps,
      sub: facts.measured
        ? [facts.criticalDetail, 'across all projects'].filter(Boolean).join(' · ')
        : 'across all projects',
    },
  ];

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Building2 className={styles.headerIcon} aria-hidden="true" />
          <div>
            <h3 className={styles.headerTitle}>Corporate Security Posture</h3>
            <p className={styles.headerSub}>
              Aggregate view across {projects} business unit project{projects === 1 ? '' : 's'}
              {companyName ? ` — ${companyName}` : ''}
            </p>
          </div>
        </div>
        {fwCount > 0 && (
          <span className={styles.headerBadge}>{fwCount} Framework{fwCount === 1 ? '' : 's'}</span>
        )}
      </div>

      <div className={styles.grid}>
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className={`${styles.card} ${styles[`tone_${c.tone}`]}`}>
              <div className={styles.cardLabelRow}>
                <Icon size={14} aria-hidden="true" className={styles.cardIcon} />
                <span className={styles.cardLabel}>{c.label}</span>
              </div>
              <div className={styles.cardValue}>{c.value}</div>
              <div className={styles.cardSub}>{c.sub}</div>

              {c.pdr && (
                <div className={styles.pdrRow}>
                  <span>P: {p != null ? p.toFixed(1) : '—'}</span>
                  <span>D: {d != null ? d.toFixed(1) : '—'}</span>
                  <span>R: {r != null ? r.toFixed(1) : '—'}</span>
                </div>
              )}

              {c.bar != null && Number.isFinite(c.bar) && (
                <div className={styles.bar}>
                  <div
                    className={styles.barFill}
                    style={{ width: `${Math.max(0, Math.min(100, c.bar))}%` }}
                  />
                </div>
              )}

              {c.key === 'gaps' && openDrift > 0 && (
                <div className={styles.driftChip}>
                  <AlertTriangle size={12} aria-hidden="true" />
                  {critical > 0 ? `${critical} critical` : `${high} high`} drift open
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
